import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import './MobileScanner.css';

function App() {
    const [message, setMessage] = useState('');
    const [encodedImageUrl, setEncodedImageUrl] = useState('');
    const [logoFile, setLogoFile] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [decodedMessage, setDecodedMessage] = useState('');
    const [decodeStatus, setDecodeStatus] = useState({ tone: 'idle', title: '', detail: '' });
    const [error, setError] = useState('');

    // Encoder options state
    const [moduleSize, setModuleSize] = useState('8');
    const [dimension, setDimension] = useState('29');
    const [quality, setQuality] = useState('25'); // Corresponds to λ

    // Camera related state
    const [showCamera, setShowCamera] = useState(false);
    const [stream, setStream] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [isMobileScannerMode, setIsMobileScannerMode] = useState(false);
    const [frozenFrame, setFrozenFrame] = useState(null);
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const overlayGuideRef = useRef(null);
    const scanLoopId = useRef(null);
    const isScanningRef = useRef(false);
    const inflightRef = useRef(false);
    const analysisCanvasRef = useRef(null);
    const lastSubmittedFrameSignatureRef = useRef(null);
    const lastDecodeAttemptAtRef = useRef(0);
    const similarFrameSkipsRef = useRef(0);

    const CAMERA_SCAN_INTERVAL_MS = 120;
    const CAMERA_CAPTURE_SIZE = 720;
    const CAMERA_JPEG_QUALITY = 0.72;
    const CAMERA_SIGNATURE_SIZE = 24;
    const CAMERA_SIMILAR_FRAME_THRESHOLD = 7;
    const CAMERA_SIMILAR_FRAME_COOLDOWN_MS = 450;
    const CAMERA_FALLBACK_CROP_RATIO = 0.68;
    const CAMERA_GUIDE_PADDING_RATIO = 0.12;

    const capacityMap = {
        '29': 81,
        '33': 107,
        '37': 136,
        '41': 169,
        '45': 205,
        '49': 244,
        '53': 255,
        '57': 255,
        '61': 255,
        '65': 255,
    };

    const MAX_MESSAGE_LENGTH = capacityMap[dimension] || 81;

    const updateDecodeStatus = (tone, title, detail = '') => {
        setDecodeStatus((prev) => (
            prev.tone === tone && prev.title === title && prev.detail === detail
                ? prev
                : { tone, title, detail }
        ));
    };

    const isNoDetectionMessage = (text = '') => {
        const normalized = text.toLowerCase();
        return normalized.includes('can not detect') || normalized.includes('cannot detect');
    };

    const resetScanHeuristics = () => {
        lastSubmittedFrameSignatureRef.current = null;
        lastDecodeAttemptAtRef.current = 0;
        similarFrameSkipsRef.current = 0;
    };

    const getAnalysisCanvas = () => {
        if (!analysisCanvasRef.current) {
            analysisCanvasRef.current = document.createElement('canvas');
        }
        const analysisCanvas = analysisCanvasRef.current;
        analysisCanvas.width = CAMERA_SIGNATURE_SIZE;
        analysisCanvas.height = CAMERA_SIGNATURE_SIZE;
        return analysisCanvas;
    };

    const createFrameSignature = (sourceCanvas) => {
        const analysisCanvas = getAnalysisCanvas();
        const analysisContext = analysisCanvas.getContext('2d', { willReadFrequently: true });
        analysisContext.drawImage(sourceCanvas, 0, 0, CAMERA_SIGNATURE_SIZE, CAMERA_SIGNATURE_SIZE);
        const { data } = analysisContext.getImageData(0, 0, CAMERA_SIGNATURE_SIZE, CAMERA_SIGNATURE_SIZE);
        const signature = new Uint8Array(CAMERA_SIGNATURE_SIZE * CAMERA_SIGNATURE_SIZE);

        for (let sourceIndex = 0, targetIndex = 0; targetIndex < signature.length; sourceIndex += 4, targetIndex += 1) {
            signature[targetIndex] = Math.round(
                data[sourceIndex] * 0.299 +
                data[sourceIndex + 1] * 0.587 +
                data[sourceIndex + 2] * 0.114
            );
        }

        return signature;
    };

    const getSignatureDifference = (previousSignature, nextSignature) => {
        if (!previousSignature || !nextSignature || previousSignature.length !== nextSignature.length) {
            return Number.POSITIVE_INFINITY;
        }

        let diffTotal = 0;
        for (let index = 0; index < nextSignature.length; index += 1) {
            diffTotal += Math.abs(previousSignature[index] - nextSignature[index]);
        }
        return diffTotal / nextSignature.length;
    };

    const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

    const getCropRegionFromGuide = (video, guideElement) => {
        const minSide = Math.min(video.videoWidth, video.videoHeight);
        const fallbackSide = minSide * CAMERA_FALLBACK_CROP_RATIO;
        const fallbackRegion = {
            sx: (video.videoWidth - fallbackSide) / 2,
            sy: (video.videoHeight - fallbackSide) / 2,
            side: fallbackSide
        };

        if (!guideElement) {
            return fallbackRegion;
        }

        const videoRect = video.getBoundingClientRect();
        const guideRect = guideElement.getBoundingClientRect();
        if (!videoRect.width || !videoRect.height || !guideRect.width || !guideRect.height) {
            return fallbackRegion;
        }

        const fit = window.getComputedStyle(video).objectFit;
        let sourceLeft;
        let sourceTop;
        let sourceWidth;
        let sourceHeight;

        if (fit === 'cover' || fit === 'contain') {
            const scale = fit === 'cover'
                ? Math.max(videoRect.width / video.videoWidth, videoRect.height / video.videoHeight)
                : Math.min(videoRect.width / video.videoWidth, videoRect.height / video.videoHeight);
            const renderedWidth = video.videoWidth * scale;
            const renderedHeight = video.videoHeight * scale;
            const insetX = (videoRect.width - renderedWidth) / 2;
            const insetY = (videoRect.height - renderedHeight) / 2;

            sourceLeft = (guideRect.left - videoRect.left - insetX) / scale;
            sourceTop = (guideRect.top - videoRect.top - insetY) / scale;
            sourceWidth = guideRect.width / scale;
            sourceHeight = guideRect.height / scale;
        } else {
            const scaleX = video.videoWidth / videoRect.width;
            const scaleY = video.videoHeight / videoRect.height;
            sourceLeft = (guideRect.left - videoRect.left) * scaleX;
            sourceTop = (guideRect.top - videoRect.top) * scaleY;
            sourceWidth = guideRect.width * scaleX;
            sourceHeight = guideRect.height * scaleY;
        }

        const paddedSide = Math.max(sourceWidth, sourceHeight) * (1 + CAMERA_GUIDE_PADDING_RATIO * 2);
        const centerX = sourceLeft + sourceWidth / 2;
        const centerY = sourceTop + sourceHeight / 2;
        const clampedSide = Math.min(paddedSide, video.videoWidth, video.videoHeight);
        const sx = clamp(centerX - clampedSide / 2, 0, video.videoWidth - clampedSide);
        const sy = clamp(centerY - clampedSide / 2, 0, video.videoHeight - clampedSide);

        return {
            sx,
            sy,
            side: clampedSide || fallbackSide
        };
    };

    useEffect(() => {
        if (message.length > MAX_MESSAGE_LENGTH) {
            setMessage(message.substring(0, MAX_MESSAGE_LENGTH));
        }
    }, [dimension, message, MAX_MESSAGE_LENGTH]);


    const handleEncode = async () => {
        setError('');
        setEncodedImageUrl('');
        
        const formData = new FormData();
        formData.append('message', message);
        formData.append('moduleSize', moduleSize);
        formData.append('dimension', dimension);
        formData.append('quality', quality);

        if (logoFile) {
            formData.append('logo', logoFile);
        }

        try {
            const apiBase = process.env.REACT_APP_API_BASE_URL || '';
            const response = await fetch(`${apiBase}/api/encode`, {
                method: 'POST',
                body: formData,
            });
            const data = await response.json();
            if (response.ok) {
                // Construct the full URL for the image if it's a relative path
                const fullImageUrl = data.imageUrl.startsWith('http') 
                    ? data.imageUrl 
                    : `${apiBase}${data.imageUrl}`;
                setEncodedImageUrl(fullImageUrl);
            } else {
                setError(data.error || 'Failed to encode.');
            }
        } catch (err) {
            setError('An error occurred during encoding.');
        }
    };

    const startCamera = async () => {
        setFrozenFrame(null);
        if (scanLoopId.current) {
            cancelAnimationFrame(scanLoopId.current);
            scanLoopId.current = null;
        }
        setDecodedMessage('');
        setError('');
        setIsScanning(false);
        isScanningRef.current = false;
        inflightRef.current = false;
        resetScanHeuristics();
        updateDecodeStatus(
            'pending',
            'Initializing camera...',
            'Waiting for camera permission and preparing the live preview.'
        );

        try {
            const constraints = { 
                video: { 
                    facingMode: 'environment',
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                }
            };
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            
            // Try to enable continuous autofocus if supported
            const track = mediaStream.getVideoTracks()[0];
            const capabilities = track.getCapabilities ? track.getCapabilities() : {};
            if (capabilities.focusMode && capabilities.focusMode.includes('continuous')) {
                try {
                    await track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
                } catch (e) {
                    console.log('Could not apply continuous focus');
                }
            }

            setStream(mediaStream);
            setShowCamera(true);
            setError('');
            inflightRef.current = false;
            updateDecodeStatus(
                'pending',
                'Camera ready. Starting backend scan...',
                'Frames inside the guide box will be sent to the backend continuously.'
            );
            
            setTimeout(async () => {
                if (videoRef.current) {
                    videoRef.current.srcObject = mediaStream;
                    try { await videoRef.current.play(); } catch (_) {}
                    const waitUntilReady = () => {
                        if (videoRef.current && videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
                            updateDecodeStatus(
                                'pending',
                                'Scanning with backend...',
                                'The app is capturing frames and sending them to the backend decoder.'
                            );
                            startAutoScan();
                        } else {
                            requestAnimationFrame(waitUntilReady);
                        }
                    };
                    waitUntilReady();
                }
            }, 50);
        } catch (err) {
            console.error('Camera error:', err);
            updateDecodeStatus(
                'danger',
                'Camera access failed.',
                'Please allow camera permissions and try again.'
            );
            setError('Unable to access camera. Please check permissions and try again.');
        }
    };

    const stopCamera = (reason = 'manual') => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        stopAutoScan();
        setShowCamera(false);
        resetScanHeuristics();
        if (reason === 'manual') {
            updateDecodeStatus(
                'info',
                'Camera stopped.',
                'Start the camera again to resume backend scanning.'
            );
        }
    };

    const startAutoScan = () => {
        setIsScanning(true);
        isScanningRef.current = true;
        scanLoopId.current = requestAnimationFrame(scanLoop);
    };

    const stopAutoScan = () => {
        setIsScanning(false);
        isScanningRef.current = false;
        if (scanLoopId.current) {
            cancelAnimationFrame(scanLoopId.current);
            scanLoopId.current = null;
        }
        inflightRef.current = false;
    };

    const scanLoop = () => {
        if (!isScanningRef.current) return;
        
        // Add a small delay/throttle to prevent overwhelming the CPU/GPU
        // but keep it fast enough for a good user experience
        setTimeout(() => {
        if (!inflightRef.current) {
            inflightRef.current = true;
            captureAndDecode().finally(() => {
                inflightRef.current = false;
                    if (isScanningRef.current) {
                scanLoopId.current = requestAnimationFrame(scanLoop);
                    }
            });
        } else {
                if (isScanningRef.current) {
            scanLoopId.current = requestAnimationFrame(scanLoop);
        }
            }
        }, CAMERA_SCAN_INTERVAL_MS);
    };
    
    const captureAndDecode = () => {
        return new Promise(resolve => {
            if (!videoRef.current || !canvasRef.current || videoRef.current.paused || videoRef.current.ended) {
                return resolve();
            }

            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            const { sx, sy, side } = getCropRegionFromGuide(video, overlayGuideRef.current);
            
            const TARGET = Math.max(Math.min(side, CAMERA_CAPTURE_SIZE), 320);

            canvas.width = TARGET;
            canvas.height = TARGET;

            if (TARGET === 0) return resolve();

            context.drawImage(video, sx, sy, side, side, 0, 0, TARGET, TARGET);

            const frameSignature = createFrameSignature(canvas);
            const signatureDiff = getSignatureDifference(
                lastSubmittedFrameSignatureRef.current,
                frameSignature
            );
            const now = Date.now();

            if (
                signatureDiff < CAMERA_SIMILAR_FRAME_THRESHOLD &&
                now - lastDecodeAttemptAtRef.current < CAMERA_SIMILAR_FRAME_COOLDOWN_MS
            ) {
                similarFrameSkipsRef.current += 1;
                if (similarFrameSkipsRef.current === 3) {
                    updateDecodeStatus(
                        'info',
                        'Waiting for a fresher frame...',
                        'Move slightly closer or tilt the PiCode a bit so the backend gets a new view.'
                    );
                }
                return resolve();
            }

            similarFrameSkipsRef.current = 0;
            lastSubmittedFrameSignatureRef.current = frameSignature;
            lastDecodeAttemptAtRef.current = now;

            canvas.toBlob(async (blob) => {
                try {
                    if (blob && isScanningRef.current) {
                        const file = new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' });
                        const formData = new FormData();
                        formData.append('image', file);
                        formData.append('source', 'camera');

                        updateDecodeStatus(
                            'pending',
                            'Checking current frame...',
                            'A compressed camera frame was sent to the backend decoder.'
                        );
                        const apiBase = process.env.REACT_APP_API_BASE_URL || '';
                        const response = await fetch(`${apiBase}/api/decode`, {
                            method: 'POST',
                            body: formData,
                        });
                        const data = await response.json();

                        if (!isScanningRef.current) {
                            return;
                        }

                        if (response.ok && data.decodedMessage) {
                            if (isNoDetectionMessage(data.decodedMessage)) {
                                updateDecodeStatus(
                                    'warning',
                                    'Backend is scanning, but no PiCode was found yet.',
                                    'Keep the code inside the guide box and try moving closer or improving lighting.'
                                );
                                return;
                            }

                            updateDecodeStatus(
                                'success',
                                'PiCode detected.',
                                'The decoded content is shown below.'
                            );

                            if (isWebUrl(data.decodedMessage)) {
                                stopCamera('success');
                                window.location.href = data.decodedMessage;
                                return;
                            }

                            // Freeze the current frame before stopping the camera
                            if (videoRef.current) {
                                try {
                                    const tempCanvas = document.createElement('canvas');
                                    tempCanvas.width = videoRef.current.videoWidth;
                                    tempCanvas.height = videoRef.current.videoHeight;
                                    tempCanvas.getContext('2d').drawImage(videoRef.current, 0, 0);
                                    setFrozenFrame(tempCanvas.toDataURL());
                                } catch (e) {
                                    console.error('Failed to capture freeze frame:', e);
                                }
                            }
                            
                            setDecodedMessage(data.decodedMessage);
                            stopCamera('success');
                        } else if (!response.ok) {
                            updateDecodeStatus(
                                'danger',
                                'Backend decode failed.',
                                'Check the backend console logs for the matching decode request.'
                            );
                        }
                    }
                } catch (err) {
                    console.error('Decode loop error:', err);
                    updateDecodeStatus(
                        'danger',
                        'Cannot reach the backend decoder.',
                        'Make sure the backend server is running, then try scanning again.'
                    );
                } finally {
                    resolve();
                }
            }, 'image/jpeg', CAMERA_JPEG_QUALITY);
        });
    };

    const handleDecode = async (file = selectedFile) => {
        if (!file) return;
        setError('');
        setDecodedMessage('');
        updateDecodeStatus(
            'pending',
            'Uploading image to backend...',
            'The selected image is being decoded on the server.'
        );
        const formData = new FormData();
        formData.append('image', file);
        formData.append('source', 'upload');

        try {
            const apiBase = process.env.REACT_APP_API_BASE_URL || '';
            const response = await fetch(`${apiBase}/api/decode`, {
                method: 'POST',
                body: formData,
            });
            const data = await response.json();
            if (response.ok) {
                if (isNoDetectionMessage(data.decodedMessage)) {
                    updateDecodeStatus(
                        'warning',
                        'Image uploaded, but no PiCode was found.',
                        'Try a sharper image or crop more tightly around the code.'
                    );
                    return;
                }
                updateDecodeStatus(
                    'success',
                    'Decode succeeded.',
                    'The decoded content is shown below.'
                );
                if (isWebUrl(data.decodedMessage)) {
                    window.location.href = data.decodedMessage;
                    return;
                }
                setDecodedMessage(data.decodedMessage);
            } else {
                updateDecodeStatus(
                    'danger',
                    'Backend decode failed.',
                    'Check the backend console logs for more detail.'
                );
                setError(data.error || 'Failed to decode.');
            }
        } catch (err) {
            updateDecodeStatus(
                'danger',
                'Cannot reach the backend decoder.',
                'Make sure the backend server is running and accessible from the browser.'
            );
            setError('An error occurred during decoding.');
        }
    };

    const isWebUrl = (str) => {
        try {
            const url = new URL(str);
            return url.protocol === 'http:' || url.protocol === 'https:';
        } catch (_) {
            return false;
        }
    };

    const renderDecodeStatus = (extraClass = '') => {
        if (!decodeStatus.title) return null;
        return (
            <div className={`decode-status ${decodeStatus.tone} ${extraClass}`.trim()}>
                <p className="decode-status-title">{decodeStatus.title}</p>
                {decodeStatus.detail && <p className="decode-status-detail">{decodeStatus.detail}</p>}
            </div>
        );
    };

    const getCameraStatusLabel = () => {
        if (decodeStatus.title) return decodeStatus.title;
        return isScanning ? 'Scanning...' : 'Initializing camera...';
    };

    const getCameraIndicatorClassName = () => {
        const tone = decodeStatus.tone && decodeStatus.tone !== 'idle' ? decodeStatus.tone : '';
        return `scanning-indicator ${isScanning ? 'scanning' : ''} ${tone}`.trim();
    };

    const shouldMergeSuccessStatus = Boolean(decodedMessage) && decodeStatus.tone === 'success';

    const downloadEncodedImage = async () => {
        if (!encodedImageUrl) return;
        try {
            const response = await fetch(encodedImageUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `PiCode-${Date.now()}.png`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch (error) {
            console.error('Error downloading the image:', error);
            setError('Could not download the image.');
        }
    };
    
    useEffect(() => {
        const query = new URLSearchParams(window.location.search);
        if (query.get('action') === 'scan') {
            setIsMobileScannerMode(true);
            // Delay slightly to ensure DOM is ready if we were doing conditional rendering
            setTimeout(() => {
                startCamera();
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const dimensionOptions = Object.keys(capacityMap).map(dim => (
        <option key={dim} value={dim}>{dim}x{dim}</option>
    ));

    // Generate the URL for the mobile scanner (current URL + ?action=scan)
    // We use window.location.href to ensure we capture the correct protocol/host/port
    const mobileScanUrl = `${window.location.origin}${window.location.pathname}?action=scan`;
    const qrCodeApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mobileScanUrl)}`;

    if (isMobileScannerMode) {
        return (
            <div className="mobile-scanner-container">
                <div className="scanner-header">
                    <h2>Scan PiCode</h2>
                    <button className="close-scanner-btn" onClick={() => {
                        stopCamera('manual');
                        setIsMobileScannerMode(false);
                    }}>
                        ✕
                    </button>
                </div>
                
                <div className="scanner-viewport">
                    <div className="video-wrapper full-screen-video">
                        {frozenFrame ? (
                            <img src={frozenFrame} className="camera-video frozen" alt="captured frame" />
                        ) : (
                            <video ref={videoRef} className="camera-video" playsInline />
                        )}
                        <div ref={overlayGuideRef} className="overlay-guide"></div>
                    </div>
                    <canvas ref={canvasRef} style={{ display: 'none' }} />
                </div>

                <div className="scanner-footer">
                   {decodedMessage ? (
                        <div className="scanner-result">
                            <h3>Decoded:</h3>
                            {isWebUrl(decodedMessage) ? (
                                <a href={decodedMessage} target="_blank" rel="noopener noreferrer">{decodedMessage}</a>
                            ) : (
                                <p>{decodedMessage}</p>
                            )}
                            <button className="primary-button" onClick={startCamera}>Scan Another</button>
                        </div>
                   ) : (
                        <>
                            <div className={getCameraIndicatorClassName()}>
                                <p>{getCameraStatusLabel()}</p>
                            </div>
                        </>
                   )}
                   {error && <p className="error-message">{error}</p>}
                </div>
            </div>
        );
    }

    return (
        <div className="App">
            <header>
                <h1>PiCode Generator</h1>
                <p className="subtitle">Secure & Artistic QR Code Generation</p>
            </header>

            <main className="main-container">
                <div className="card encoder-card">
                <h2>Encoder</h2>
                <div className="textarea-container">
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Enter your message (e.g., a URL)"
                        maxLength={MAX_MESSAGE_LENGTH}
                    />
                    <span className="char-counter">{message.length} / {MAX_MESSAGE_LENGTH}</span>
                </div>
                
                <div className="options">
                    <div>
                        <label htmlFor="logo-upload">Logo Image:</label>
                        <input id="logo-upload" type="file" onChange={(e) => setLogoFile(e.target.files[0])} accept="image/*" />
                    </div>
                    <div>
                        <label htmlFor="dimension-select">Dimensions:</label>
                        <select id="dimension-select" value={dimension} onChange={(e) => setDimension(e.target.value)}>
                            {dimensionOptions}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="quality-slider">Quality (λ): {quality}</label>
                        <input 
                            id="quality-slider"
                            type="range" 
                            min="10" 
                            max="40" 
                            value={quality} 
                            onChange={(e) => setQuality(e.target.value)}
                            className="slider"
                        />
                    </div>
                    <div>
                        <label htmlFor="module-size-select">Module Size:</label>
                        <select id="module-size-select" value={moduleSize} onChange={(e) => setModuleSize(e.target.value)}>
                            <option value="4">4x4 pixels</option>
                            <option value="5">5x5 pixels</option>
                            <option value="6">6x6 pixels</option>
                            <option value="7">7x7 pixels</option>
                            <option value="8">8x8 pixels</option>
                        </select>
                    </div>
                </div>

                <button onClick={handleEncode} disabled={!message || !logoFile}>Generate PiCode</button>

                {encodedImageUrl && (
                    <div className="result">
                        <h3>Your PiCode:</h3>
                        <img src={encodedImageUrl} alt="Encoded PiCode" />
                        <button className="download-btn" onClick={downloadEncodedImage}>Download Image</button>
                    </div>
                )}
            </div>

            <div className="card decoder-card">
                <h2>Decoder</h2>
                <div className="decode-options">
                    <div className="file-upload-section">
                        <h3>Upload an Image</h3>
                        <input type="file" onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                                setSelectedFile(file);
                                handleDecode(file);
                            }
                        }} accept="image/*" />
                    </div>
                    <div className="camera-section">
                        <h3>Use Camera</h3>
                        {!showCamera ? (
                            <button onClick={startCamera} className="primary-button">Start Camera</button>
                        ) : (
                            <button onClick={stopCamera} className="stop-button">Stop Camera</button>
                        )}
                    </div>
                </div>
                {showCamera && (
                    <div className="camera-container">
                        <div className="video-wrapper">
                            <video ref={videoRef} className="camera-video" playsInline />
                            <div ref={overlayGuideRef} className="overlay-guide"></div>
                        </div>
                        <canvas ref={canvasRef} style={{ display: 'none' }} />
                        <div className={getCameraIndicatorClassName()}>
                            <p>{getCameraStatusLabel()}</p>
                        </div>
                    </div>
                )}
                {!showCamera && !shouldMergeSuccessStatus && renderDecodeStatus()}
                {decodedMessage && (
                    <div className={`result ${shouldMergeSuccessStatus ? 'decoded-result' : ''}`.trim()}>
                        <h3>{shouldMergeSuccessStatus ? decodeStatus.title : 'Decoded Message:'}</h3>
                        {shouldMergeSuccessStatus && decodeStatus.detail && (
                            <p className="result-subtitle">{decodeStatus.detail}</p>
                        )}
                        {isWebUrl(decodedMessage) ? (
                            <a href={decodedMessage} target="_blank" rel="noopener noreferrer">{decodedMessage}</a>
                        ) : (
                            <p>{decodedMessage}</p>
                        )}
                    </div>
                )}
                
                <div className="mobile-qr-section">
                    <h3>Scan to Open on Mobile</h3>
                    <div className="qr-container">
                        <img src={qrCodeApiUrl} alt="Scan to open on mobile" />
                        <p className="qr-hint">Scan with your phone to use the camera remotely</p>
                    </div>
                </div>
            </div>
            </main>
             {error && <p className="error">{error}</p>}
        </div>
    );
}

export default App;
